import React, { useState, useEffect } from "react";
import { ShieldCheck, Ban, ShieldOff, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import { Loading, Empty, SectionHeader, Card, fmtDate } from "./shared";
import { SelectableTable, SelectAllHeaderCheckbox, RowCheckbox } from "@/components/admin/SelectableTable";

const UNBLOCK_PHRASE = "ENABLE AI FOR USER";

export default function AdminUsers() {
  const [users, setUsers] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(null);
  const load = () => api.get("/admin/users").then((r) => setUsers(r.data.users || []));
  const act = async (u, status) => {
    setBusy(u.id); setError("");
    try {
      if (status === "retry") {
        const { data } = await api.post(`/admin/users/${u.id}/approval-notification`);
        if (!data.sent) setError("Email non envoyé : vérifier la configuration Resend et réessayer.");
      } else await api.post(`/admin/users/${u.id}/approval`, { status });
      await load();
    } catch (e) { setError(e.response?.data?.detail || "Action impossible. Réessayez."); }
    finally { setBusy(null); }
  };

  const [busyId, setBusyId] = useState(null);
  const [unblockTarget, setUnblockTarget] = useState(null); // user object
  const [phrase, setPhrase] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/admin/users").then((r) => setUsers(r.data.users || [])).catch(() => setUsers([]));
  }, []);

  const applyRow = (u) => setUsers((prev) => (prev || []).map((x) => (x.id === u.user_id
    ? { ...x, ai_admin_blocked: u.blocked, ai_admin_blocked_at: u.blocked_at, ai_admin_block_reason: u.block_reason, ai_admin_unblocked_at: u.unblocked_at }
    : x)));

  const blockUser = async (u) => {
    if (!window.confirm(`Bloquer l'accès IA pour ${u.email} ? Le blocage est immédiat.`)) return;
    const reason = (window.prompt("Motif (optionnel) :", "") || "").trim();
    setBusyId(u.id); setErr("");
    try {
      const r = await api.patch(`/admin/users/${u.id}/ai-access`, { blocked: true, reason });
      applyRow(r.data);
    } catch (e) {
      setErr("Échec du blocage. Réessayez.");
    } finally { setBusyId(null); }
  };

  const confirmUnblock = async () => {
    if (!unblockTarget || phrase !== UNBLOCK_PHRASE) return;
    const u = unblockTarget;
    setBusyId(u.id); setErr("");
    try {
      const r = await api.patch(`/admin/users/${u.id}/ai-access`, { blocked: false, confirmation: phrase });
      applyRow(r.data);
      setUnblockTarget(null); setPhrase("");
    } catch (e) {
      setErr("Échec de la réautorisation. Vérifiez la phrase de confirmation.");
    } finally { setBusyId(null); }
  };

  if (users === null) return <Loading />;

  return (
    <div data-testid="admin-users">
      <SectionHeader title="Users" subtitle="Validation des inscriptions et contrôle de l’accès IA par utilisateur." />
      {error && <p role="alert" className="my-3 text-red-700">{error}</p>}
      {err && <div className="mb-3 rounded-lg bg-red-50 px-4 py-2 text-sm text-red-700" data-testid="admin-users-error">{err}</div>}
      {users.length === 0 ? <Empty label="Aucun utilisateur." testid="users-empty" /> : (
        <Card>
          <SelectableTable rows={users} rowId={(u) => u.id} filename="users.csv" ariaLabel="Utilisateurs — sélection et défilement" csvColumns={[{ label: "Nom", get: (u) => u.name }, { label: "E-mail", get: (u) => u.email }, { label: "Organisations", get: (u) => u.org_count }, { label: "Rôle", get: (u) => (u.is_platform_admin ? "Admin plateforme" : "Utilisateur") }, { label: "Accès IA", get: (u) => (u.ai_admin_blocked ? "IA bloquée" : "IA autorisée") }]}>
            <table className="w-full min-w-[900px]">
              <thead><tr className="border-b border-slate-200 bg-slate-50 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                <th className="px-5 py-3"><SelectAllHeaderCheckbox /></th><th className="px-5 py-3">Nom</th><th className="px-5 py-3">E-mail</th>
                <th className="px-5 py-3">Organisations</th><th className="px-5 py-3">Rôle</th>
                <th className="px-5 py-3">Accès IA</th><th className="px-5 py-3">Action IA</th><th className="px-5 py-3">Accès plateforme</th>
              </tr></thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-b border-slate-100 last:border-0">
                    <td className="px-5 py-3"><RowCheckbox id={u.id} label={`Sélectionner ${u.email}`} /></td>
                    <td className="px-5 py-3 text-sm font-medium text-[#17201A]">{u.name || "—"}</td>
                    <td className="px-5 py-3 text-sm text-slate-600">{u.email}</td>
                    <td className="px-5 py-3 text-sm text-slate-600">{u.org_count}</td>
                    <td className="px-5 py-3">
                      {u.is_platform_admin
                        ? <span className="inline-flex items-center gap-1 rounded-full bg-[#E8F0EA] px-2 py-0.5 text-xs font-semibold text-[#3E7256]"><ShieldCheck size={12} /> Admin plateforme</span>
                        : <span className="text-xs text-slate-400">Utilisateur</span>}
                    </td>
                    <td className="px-5 py-3">
                      {u.ai_admin_blocked ? (
                        <div className="flex flex-col" data-testid={`user-ai-status-${u.id}`}>
                          <span className="inline-flex w-fit items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700"><ShieldOff size={12} /> IA bloquée</span>
                          {u.ai_admin_blocked_at && <span className="mt-1 text-[11px] text-slate-400">Depuis {fmtDate(u.ai_admin_blocked_at)}</span>}
                          {u.ai_admin_block_reason && <span className="mt-0.5 max-w-[220px] truncate text-[11px] text-slate-500" title={u.ai_admin_block_reason}>Motif : {u.ai_admin_block_reason}</span>}
                        </div>
                      ) : (
                        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700" data-testid={`user-ai-status-${u.id}`}><CheckCircle2 size={12} /> IA autorisée</span>
                      )}
                    </td>
                    <td className="px-5 py-3">
                      {u.ai_admin_blocked ? (
                        <button onClick={() => { setUnblockTarget(u); setPhrase(""); setErr(""); }} disabled={busyId === u.id}
                          data-testid={`user-ai-unblock-btn-${u.id}`}
                          className="inline-flex items-center gap-1 rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-100 disabled:opacity-50">
                          <ShieldCheck size={13} /> Réautoriser l&apos;IA
                        </button>
                      ) : (
                        <button onClick={() => blockUser(u)} disabled={busyId === u.id}
                          data-testid={`user-ai-block-btn-${u.id}`}
                          className="inline-flex items-center gap-1 rounded-lg border border-red-300 bg-white px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50">
                          <Ban size={13} /> Bloquer l&apos;IA
                        </button>
                      )}
                    </td>
                    <td className="px-5 py-3 text-sm">
                      {{ pending: "En attente", approved: "Autorisé", rejected: "Refusé" }[u.approval_status] || "Autorisé"}
                      {u.approval_status === "pending" && <div className="mt-2 flex flex-wrap gap-2">
                        <button disabled={busy === u.id} onClick={() => act(u, "approved")} className="rounded bg-[#1E3D2B] px-3 py-1 text-white disabled:opacity-50">Accepter</button>
                        <button disabled={busy === u.id} onClick={() => act(u, "rejected")} className="rounded border px-3 py-1 disabled:opacity-50">Refuser</button>
                        {u.approval_notification_sent !== true && <button disabled={busy === u.id} onClick={() => act(u, "retry")} className="text-amber-800 underline">Alerte email non envoyée — réessayer</button>}
                      </div>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </SelectableTable>
        </Card>
      )}

      {unblockTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="user-ai-unblock-modal">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
            <h3 className="text-base font-bold text-[#17201A]">Réautoriser l&apos;IA</h3>
            <p className="mt-1 text-sm text-slate-600">Réautoriser l&apos;accès IA pour <b>{unblockTarget.email}</b>.</p>
            <p className="mt-2 text-xs text-slate-500">Saisissez la phrase exacte <code className="rounded bg-slate-100 px-1">{UNBLOCK_PHRASE}</code> pour confirmer.</p>
            <input value={phrase} onChange={(e) => setPhrase(e.target.value)} placeholder={UNBLOCK_PHRASE}
              data-testid="user-ai-unblock-phrase"
              className="mt-3 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-[#3E7256] focus:outline-none" />
            <div className="mt-4 flex items-center justify-end gap-2">
              <button onClick={() => { setUnblockTarget(null); setPhrase(""); }} data-testid="user-ai-unblock-cancel"
                className="rounded-lg px-3 py-1.5 text-sm font-semibold text-slate-500 hover:bg-slate-100">Annuler</button>
              <button onClick={confirmUnblock} disabled={busyId === unblockTarget.id || phrase !== UNBLOCK_PHRASE}
                data-testid="user-ai-unblock-confirm"
                className="rounded-lg bg-[#3E7256] px-4 py-1.5 text-sm font-semibold text-white hover:bg-[#33604A] disabled:opacity-50">Réautoriser</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
